// src/pages/SellerSuccess/styles.js
import { COLORS, LAYOUT, TYPOGRAPHY } from '../../constants';

export const sellerSuccessStyles = {
  // Main page container
  page: {
    minHeight: '100vh',
    backgroundColor: COLORS.grayLight,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: `${LAYOUT.spacing.xl} ${LAYOUT.spacing.lg}`,
  },

  // Success container
  container: {
    width: '100%',
    maxWidth: '700px',
    backgroundColor: COLORS.white,
    borderRadius: LAYOUT.borderRadius.xl,
    padding: `${LAYOUT.spacing.xxxl} ${LAYOUT.spacing.xxl}`,
    boxShadow: '0 20px 60px -12px rgba(0, 0, 0, 0.15), 0 8px 32px -8px rgba(0, 0, 0, 0.1)',
    border: `1px solid ${COLORS.border}`,
    textAlign: 'center',
  },

  // Status content wrapper
  statusContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: LAYOUT.spacing.lg,
  },

  // Loading spinner
  loadingSpinner: {
    width: '60px',
    height: '60px',
    border: `4px solid ${COLORS.border}`,
    borderTop: `4px solid ${COLORS.primary}`,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: LAYOUT.spacing.md,
  },

  loadingText: {
    fontSize: '1.1rem',
    color: COLORS.text.secondary,
    fontWeight: '500',
  },

  // Hero Section
  heroSection: {
    marginBottom: LAYOUT.spacing.xxl,
  },

  iconContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '120px',
    height: '120px',
    backgroundColor: `${COLORS.success}15`,
    borderRadius: '50%',
    margin: `0 auto ${LAYOUT.spacing.xl}`,
    color: COLORS.success,
  },

  // Typography
  title: {
    ...TYPOGRAPHY.heading1,
    fontSize: '3rem',
    fontWeight: '800',
    color: COLORS.text.primary,
    marginBottom: LAYOUT.spacing.md,
    lineHeight: '1.1',
    letterSpacing: '-0.02em',
  },

  description: {
    fontSize: '1.3rem',
    color: COLORS.text.secondary,
    lineHeight: '1.65',
    maxWidth: '580px',
    margin: '0 auto',
    fontWeight: '400',
  },

  // Success animation
  successAnimation: {
    marginBottom: LAYOUT.spacing.lg,
  },

  successIcon: {
    fontSize: '4rem',
    color: COLORS.success,
    animation: 'celebrate 1.5s ease-in-out infinite',
  },

  // Features section
  featuresSection: {
    marginBottom: LAYOUT.spacing.xxl,
  },

  featuresGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: LAYOUT.spacing.lg,
    marginBottom: LAYOUT.spacing.xl,
  },

  featureCard: {
    textAlign: 'center',
    padding: LAYOUT.spacing.xl,
    backgroundColor: COLORS.grayLight,
    borderRadius: LAYOUT.borderRadius.lg,
    border: `1px solid ${COLORS.border}`,
    transition: 'all 0.3s ease',
  },

  featureIcon: {
    width: '48px',
    height: '48px',
    backgroundColor: COLORS.primary,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: `0 auto ${LAYOUT.spacing.md}`,
    color: COLORS.white,
    fontSize: '1.5rem',
  },

  featureTitle: {
    fontSize: '1.1rem',
    fontWeight: '600',
    color: COLORS.text.primary,
    marginBottom: LAYOUT.spacing.sm,
  },

  featureDescription: {
    fontSize: '0.9rem',
    color: COLORS.text.secondary,
    lineHeight: '1.5',
  },

  // CTA Section
  ctaSection: {
    textAlign: 'center',
  },

  primaryButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: LAYOUT.spacing.sm,
    minWidth: '280px',
    padding: `${LAYOUT.spacing.lg} ${LAYOUT.spacing.xxxl}`,
    backgroundColor: COLORS.primary,
    color: COLORS.white,
    border: 'none',
    borderRadius: '50px',
    fontSize: '1.1rem',
    fontWeight: '600',
    letterSpacing: '0.5px',
    cursor: 'pointer',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    textDecoration: 'none',
    boxShadow: `0 4px 24px rgba(206, 17, 38, 0.3), 0 2px 8px rgba(206, 17, 38, 0.2)`,
    textTransform: 'uppercase',
    transform: 'translateY(0)',
    marginBottom: LAYOUT.spacing.lg,
  },

  // Redirect notice
  redirectNotice: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: LAYOUT.spacing.sm,
    color: COLORS.text.secondary,
    fontSize: '1rem',
    padding: `${LAYOUT.spacing.lg} ${LAYOUT.spacing.xl}`,
    backgroundColor: `${COLORS.primary}08`,
    borderRadius: LAYOUT.borderRadius.lg,
    border: `1px solid ${COLORS.primary}20`,
  },

  redirectIcon: {
    fontSize: '1.2rem',
    animation: 'rocket-launch 2s ease-in-out infinite',
  },

  // Error state
  errorIconContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '120px',
    height: '120px',
    backgroundColor: '#fee2e2',
    borderRadius: '50%',
    margin: `0 auto ${LAYOUT.spacing.xl}`,
    color: '#dc2626',
  },

  errorIcon: {
    fontSize: '4rem',
  },

  errorDetail: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: LAYOUT.borderRadius.lg,
    padding: LAYOUT.spacing.lg,
    color: '#dc2626',
    fontSize: '1rem',
    margin: `${LAYOUT.spacing.lg} 0`,
    lineHeight: '1.5',
  },

  // Retry button
  retryButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: LAYOUT.spacing.sm,
    minWidth: '200px',
    padding: `${LAYOUT.spacing.lg} ${LAYOUT.spacing.xl}`,
    backgroundColor: COLORS.primary,
    color: COLORS.white,
    border: 'none',
    borderRadius: '50px',
    fontSize: '1rem',
    fontWeight: '600',
    letterSpacing: '0.5px',
    cursor: 'pointer',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    textTransform: 'uppercase',
    transform: 'translateY(0)',
    boxShadow: `0 4px 16px rgba(206, 17, 38, 0.3)`,
  },

  retryButtonHover: {
    backgroundColor: COLORS.primaryDark,
    transform: 'translateY(-2px)',
    boxShadow: `0 8px 24px rgba(206, 17, 38, 0.4)`,
  },
};

// CSS-in-JS animations
export const sellerSuccessAnimations = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  @keyframes celebrate {
    0%, 100% { transform: rotate(-5deg) scale(1); }
    50% { transform: rotate(5deg) scale(1.1); }
  }

  @keyframes rocket-launch {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-3px); }
  }
`;